/**
 * LinkInBioPage — Editor für die öffentliche „Link-in-Bio"-Seite des Workspaces.
 *
 * Authentifiziert + strikt workspace-isoliert (Backend: get_current_workspace_id).
 * Lädt/speichert über /api/link-in-bio/profile. Live-Vorschau, Slug-Verfügbarkeits-
 * prüfung, Link-Verwaltung (hinzufügen/sortieren/aktivieren), saubere Fehler-UX.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Link2, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Copy, ExternalLink,
  Loader2, Save, CheckCircle2, AlertCircle, Globe, Upload, Cloud, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
// Basis für hochgeladene Bilder: das Backend liefert einen relativen Pfad
// (/api/media/file/…). Im Betrieb liegt das Backend hinter derselben Domain,
// bei getrenntem Origin bleibt das Bild so trotzdem auffindbar.
const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL || "";

const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const THEME_PRESETS = [
  "#EA580C", "#2563EB", "#059669", "#7C3AED",
  "#DB2777", "#0891B2", "#CA8A04", "#475569",
];

const EMPTY_FORM = {
  slug: "",
  title: "",
  description: "",
  avatar_url: "",
  theme_color: "#EA580C",
  published: true,
  links: [],
};

// Liest eine kundenfreundliche Fehlermeldung aus einer Axios-Antwort (kein Leak).
function apiError(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return String(d[0].msg).replace(/^Value error,\s*/i, "");
  return fallback;
}

function newLink() {
  return { id: `tmp-${Math.random().toString(36).slice(2)}`, label: "", url: "", active: true };
}

// Leichte Client-Prüfung (Spiegel der Server-Regel: nur http/https). Verbessert das
// Feedback und verhindert, dass unsichere/kaputte URLs in der Vorschau gerendert werden.
// Die verbindliche Validierung bleibt serverseitig (models.ProfileIn).
const isHttpUrl = (v) => /^https?:\/\/\S+$/i.test((v || "").trim());

// Hochgeladene Bilder liegen NICHT unter http(s), sondern unter dem eigenen
// Medien-Pfad des Backends. Die Prüfung ist bewusst am vollständigen Präfix
// verankert — „alles was mit / anfängt" wäre unsicher, weil protokollrelative
// Eingaben wie //fremde-seite.tld/x sonst durchrutschen würden.
// Spiegel der Server-Regel (link_in_bio/models.py: UPLOADED_MEDIA_URL_RE), damit
// der Client nicht „ok" sagt, wo der Server später ablehnt.
const isUploadedImagePath = (v) =>
  /^\/api\/media\/file\/[0-9a-f]{24}\?t=[A-Za-z0-9_-]{16,128}$/.test((v || "").trim());

// Darf als Bild gerendert werden: echte http(s)-Adresse ODER eigener Upload.
const isImageSrc = (v) => isHttpUrl(v) || isUploadedImagePath(v);

// Anzeige-Quelle: Upload-Pfade brauchen die Backend-Basis davor.
const imageSrc = (v) => {
  const raw = (v || "").trim();
  return isUploadedImagePath(raw) ? `${MEDIA_BASE}${raw}` : raw;
};

export default function LinkInBioPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [savedSlug, setSavedSlug] = useState("");
  const [slugStatus, setSlugStatus] = useState({ checking: false, valid: true, available: true });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const slugTimer = useRef(null);
  const avatarFileRef = useRef(null);

  const publicUrl = useMemo(
    () => (form.slug ? `${window.location.origin}/p/${form.slug}` : ""),
    [form.slug],
  );

  // ── Laden ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/link-in-bio/profile`)
      .then((res) => {
        if (!active) return;
        const p = res.data?.profile;
        if (p) {
          setForm({
            slug: p.slug || "",
            title: p.title || "",
            description: p.description || "",
            avatar_url: p.avatar_url || "",
            theme_color: p.theme_color || "#EA580C",
            published: p.published !== false,
            links: (p.links || []).map((l) => ({
              id: l.id || newLink().id,
              label: l.label || "",
              url: l.url || "",
              active: l.active !== false,
            })),
          });
          setHasProfile(true);
          setSavedSlug(p.slug || "");
        }
      })
      .catch(() => {
        if (active) toast.error("Profil konnte nicht geladen werden.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // ── Slug-Verfügbarkeit (debounced) ──────────────────────────────────────────
  useEffect(() => {
    if (slugTimer.current) clearTimeout(slugTimer.current);
    const raw = form.slug.trim();
    if (!raw) {
      setSlugStatus({ checking: false, valid: false, available: true });
      return;
    }
    setSlugStatus((s) => ({ ...s, checking: true }));
    slugTimer.current = setTimeout(() => {
      axios
        .get(`${API}/link-in-bio/slug-available`, { params: { slug: raw } })
        .then((res) => {
          setSlugStatus({
            checking: false,
            valid: !!res.data?.valid,
            available: !!res.data?.available,
          });
          // Server normalisiert den Slug — übernehmen, falls abweichend
          if (res.data?.slug && res.data.slug !== form.slug) {
            setForm((f) => ({ ...f, slug: res.data.slug }));
          }
        })
        .catch(() => setSlugStatus({ checking: false, valid: false, available: true }));
    }, 450);
    return () => slugTimer.current && clearTimeout(slugTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.slug]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const updateLink = (idx, patch) =>
    setForm((f) => ({ ...f, links: f.links.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));

  const addLink = () => setForm((f) => ({ ...f, links: [...f.links, newLink()] }));

  const removeLink = (idx) =>
    setForm((f) => ({ ...f, links: f.links.filter((_, i) => i !== idx) }));

  const moveLink = (idx, dir) =>
    setForm((f) => {
      const links = [...f.links];
      const j = idx + dir;
      if (j < 0 || j >= links.length) return f;
      [links[idx], links[j]] = [links[j], links[idx]];
      return { ...f, links };
    });

  const copyPublicUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link kopiert");
    } catch {
      toast.error("Kopieren nicht möglich — bitte manuell markieren.");
    }
  };

  // ── Profilbild hochladen ────────────────────────────────────────────────────
  // Gleiches Muster wie der Bild-Upload im Monatsplan: Typ + Größe zuerst im
  // Browser prüfen (schnelles, verständliches Feedback), dann FormData an den
  // Server. Fehler werden sichtbar gemeldet — kein stiller Fehlschlag.
  const handleAvatarFile = async (file) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Nur JPG, PNG oder WebP möglich.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(`Das Bild ist ${(file.size / 1024 / 1024).toFixed(1)} MB groß — erlaubt sind 15 MB.`);
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/media/upload`, fd);
      const url = res.data?.image_url;
      if (!res.data?.success || !url) {
        toast.error("Das Bild wurde nicht angenommen. Bitte später erneut versuchen.");
        return;
      }
      setField("avatar_url", url);
      toast.success("Bild hochgeladen — zum Übernehmen noch speichern.");
    } catch (err) {
      toast.error(apiError(err, "Bild konnte nicht hochgeladen werden. Bitte später erneut versuchen."));
    } finally {
      setUploadingAvatar(false);
    }
  };

  // ── Speichern ───────────────────────────────────────────────────────────────
  const canSave =
    form.title.trim() && form.slug.trim() && slugStatus.valid && slugStatus.available && !saving;

  const save = async () => {
    if (!form.title.trim()) return toast.error("Bitte einen Titel angeben.");
    if (!slugStatus.valid) return toast.error("Bitte eine gültige Profil-Adresse wählen.");
    if (!slugStatus.available) return toast.error("Diese Profil-Adresse ist bereits vergeben.");
    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        description: form.description.trim(),
        avatar_url: form.avatar_url.trim() || null,
        theme_color: form.theme_color,
        published: form.published,
        links: form.links
          .filter((l) => l.label.trim() && l.url.trim())
          .map((l, i) => ({ label: l.label.trim(), url: l.url.trim(), active: l.active, order: i })),
      };
      const res = await axios.put(`${API}/link-in-bio/profile`, payload);
      const p = res.data?.profile;
      setHasProfile(true);
      setSavedSlug(p?.slug || payload.slug);
      toast.success("Gespeichert ✓");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setSlugStatus((s) => ({ ...s, available: false }));
        toast.error("Diese Profil-Adresse ist bereits vergeben. Bitte eine andere wählen.");
      } else if (status === 403) {
        toast.error(apiError(err, "Diese Funktion ist in deinem Tarif nicht enthalten."));
      } else {
        toast.error(apiError(err, "Speichern fehlgeschlagen. Bitte später erneut versuchen."));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin text-stone-400" size={28} />
      </div>
    );
  }

  const theme = form.theme_color || "#EA580C";
  const previewLinks = form.links.filter((l) => l.active && l.label.trim() && l.url.trim());

  return (
    <div className="max-w-6xl mx-auto">
      {/* Kopf */}
      <div className="flex items-start gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
          <Link2 size={20} className="text-fox-fire" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Link-in-Bio</h1>
          <p className="text-stone-500 text-sm">
            Eine schlanke, öffentliche Seite mit all deinen wichtigsten Links — perfekt für die Bio
            auf Instagram, TikTok & Co.
          </p>
        </div>
      </div>

      {/* Öffentliche Adresse */}
      {hasProfile && savedSlug && (
        <div className="mb-6 mt-3 flex flex-col sm:flex-row sm:items-center gap-3 bg-stone-50 border border-stone-200 rounded-xl p-4">
          <Globe size={18} className="text-fox-fire flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-stone-400">Deine öffentliche Adresse</div>
            <div className="text-sm font-medium text-stone-800 truncate">{`${window.location.origin}/p/${savedSlug}`}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={copyPublicUrl}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-stone-200 text-sm text-stone-700 hover:bg-stone-100"
            >
              <Copy size={15} /> Kopieren
            </button>
            <a
              href={`/p/${savedSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-stone-200 text-sm text-stone-700 hover:bg-stone-100"
            >
              <ExternalLink size={15} /> Öffnen
            </a>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-4">
        {/* ── Editor ── */}
        <div className="space-y-6">
          {/* Basis */}
          <section className="bg-white rounded-xl border border-stone-200 p-5">
            <h2 className="font-semibold text-stone-800 mb-4">Basis</h2>

            <label className="block text-sm font-medium text-stone-700 mb-1">Profil-Adresse</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-400 whitespace-nowrap">{window.location.host}/p/</span>
              <input
                value={form.slug}
                onChange={(e) => setField("slug", e.target.value.toLowerCase())}
                placeholder="dein-name"
                data-testid="lib-slug"
                className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-stone-300 focus:border-fox-fire focus:ring-1 focus:ring-fox-fire outline-none text-sm"
              />
            </div>
            <div className="h-5 mt-1 text-xs flex items-center gap-1">
              {form.slug.trim() && slugStatus.checking && (
                <span className="text-stone-400">Prüfe Verfügbarkeit …</span>
              )}
              {form.slug.trim() && !slugStatus.checking && !slugStatus.valid && (
                <span className="text-red-500 flex items-center gap-1">
                  <AlertCircle size={13} /> 3–40 Zeichen, nur a–z, 0–9, Bindestrich.
                </span>
              )}
              {form.slug.trim() && !slugStatus.checking && slugStatus.valid && !slugStatus.available && (
                <span className="text-red-500 flex items-center gap-1">
                  <AlertCircle size={13} /> Schon vergeben — bitte andere wählen.
                </span>
              )}
              {form.slug.trim() && !slugStatus.checking && slugStatus.valid && slugStatus.available && (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle2 size={13} /> Verfügbar
                </span>
              )}
            </div>

            <label className="block text-sm font-medium text-stone-700 mb-1 mt-3">Titel</label>
            <input
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="z. B. Mein Coaching"
              maxLength={80}
              data-testid="lib-title"
              className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-fox-fire focus:ring-1 focus:ring-fox-fire outline-none text-sm"
            />

            <label className="block text-sm font-medium text-stone-700 mb-1 mt-3">
              Kurzbeschreibung <span className="text-stone-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Ein, zwei Sätze, die dich oder dein Angebot beschreiben."
              maxLength={280}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-fox-fire focus:ring-1 focus:ring-fox-fire outline-none text-sm resize-none"
            />
            <div className="text-right text-xs text-stone-400">{form.description.length}/280</div>

            <label className="block text-sm font-medium text-stone-700 mb-1 mt-3">
              Profilbild <span className="text-stone-400 font-normal">(optional)</span>
            </label>

            {/* Datei-Upload */}
            <input
              ref={avatarFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid="lib-avatar-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = ""; // gleiche Datei soll erneut wählbar bleiben
                handleAvatarFile(file);
              }}
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => avatarFileRef.current?.click()}
                disabled={uploadingAvatar}
                data-testid="lib-avatar-upload"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingAvatar ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                {uploadingAvatar ? "Wird hochgeladen …" : "Bild hochladen"}
              </button>

              {/* OneDrive: bewusst deaktiviert — es gibt (noch) keine Anbindung.
                  Kein Knopf, der so tut als ob. */}
              <span
                aria-disabled="true"
                title="OneDrive-Anbindung ist noch nicht freigeschaltet."
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-stone-200 bg-stone-50 text-sm text-stone-400 cursor-not-allowed select-none"
                data-testid="lib-avatar-onedrive"
              >
                <Cloud size={15} /> Aus OneDrive
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-stone-200 text-stone-500">
                  noch nicht verfügbar
                </span>
              </span>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">JPG, PNG oder WebP, bis 15 MB.</p>
            <p className="text-xs text-stone-400 mt-1 flex items-start gap-1.5">
              <Info size={12} className="mt-0.5 flex-shrink-0" />
              <span>
                Die OneDrive-Anbindung ist noch nicht freigeschaltet. Tipp: Auf Windows liegt
                OneDrive als ganz normaler Ordner im Datei-Dialog — wähle dein Bild einfach
                über „Bild hochladen" aus.
              </span>
            </p>

            {/* Gleichwertige Alternative: fertige Bild-Adresse einfügen */}
            <label className="block text-sm font-medium text-stone-700 mb-1 mt-3">
              Oder Bild-Adresse (URL) einfügen
            </label>
            <input
              value={form.avatar_url}
              onChange={(e) => setField("avatar_url", e.target.value)}
              placeholder="https://…/bild.jpg"
              className={`w-full px-3 py-2 rounded-lg border outline-none text-sm focus:ring-1 focus:ring-fox-fire ${
                form.avatar_url.trim() && !isImageSrc(form.avatar_url)
                  ? "border-red-300 focus:border-red-400"
                  : "border-stone-300 focus:border-fox-fire"
              }`}
            />
            {form.avatar_url.trim() && !isImageSrc(form.avatar_url) && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle size={12} /> Bitte eine vollständige http(s)-Adresse angeben — oder ein Bild hochladen.
              </p>
            )}
            {isUploadedImagePath(form.avatar_url) && (
              <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                <CheckCircle2 size={12} className="text-green-600" /> Hochgeladenes Bild wird verwendet.
              </p>
            )}
          </section>

          {/* Design */}
          <section className="bg-white rounded-xl border border-stone-200 p-5">
            <h2 className="font-semibold text-stone-800 mb-3">Farbe</h2>
            <div className="flex flex-wrap items-center gap-2">
              {THEME_PRESETS.map((c) => (
                <button
                  key={c}
                  onClick={() => setField("theme_color", c)}
                  aria-label={`Farbe ${c}`}
                  className={`w-8 h-8 rounded-full border-2 transition ${
                    theme.toLowerCase() === c.toLowerCase() ? "border-stone-800 scale-110" : "border-white shadow"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <input
                type="color"
                value={theme}
                onChange={(e) => setField("theme_color", e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border border-stone-200"
                title="Eigene Farbe"
              />
            </div>
          </section>

          {/* Links */}
          <section className="bg-white rounded-xl border border-stone-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-stone-800">Links</h2>
              <span className="text-xs text-stone-400">{form.links.length}/30</span>
            </div>

            {form.links.length === 0 && (
              <p className="text-sm text-stone-400 mb-3">
                Noch keine Links. Füge deinen ersten Link hinzu — z. B. deine Website, einen Termin-Link
                oder dein Social-Profil.
              </p>
            )}

            <div className="space-y-3">
              {form.links.map((l, idx) => (
                <div key={l.id} className="flex items-start gap-2 bg-stone-50 rounded-lg p-3">
                  <div className="flex flex-col gap-1 pt-1">
                    <button
                      onClick={() => moveLink(idx, -1)}
                      disabled={idx === 0}
                      className="text-stone-400 hover:text-stone-700 disabled:opacity-30"
                      aria-label="Nach oben"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      onClick={() => moveLink(idx, 1)}
                      disabled={idx === form.links.length - 1}
                      className="text-stone-400 hover:text-stone-700 disabled:opacity-30"
                      aria-label="Nach unten"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <input
                      value={l.label}
                      onChange={(e) => updateLink(idx, { label: e.target.value })}
                      placeholder="Bezeichnung (z. B. Website)"
                      maxLength={60}
                      className="w-full px-3 py-1.5 rounded-lg border border-stone-300 focus:border-fox-fire focus:ring-1 focus:ring-fox-fire outline-none text-sm"
                    />
                    <input
                      value={l.url}
                      onChange={(e) => updateLink(idx, { url: e.target.value })}
                      placeholder="https://…"
                      className={`w-full px-3 py-1.5 rounded-lg border outline-none text-sm focus:ring-1 focus:ring-fox-fire ${
                        l.url.trim() && !isHttpUrl(l.url)
                          ? "border-red-300 focus:border-red-400"
                          : "border-stone-300 focus:border-fox-fire"
                      }`}
                    />
                    {l.url.trim() && !isHttpUrl(l.url) && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle size={12} /> Link muss mit http(s):// beginnen.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-2 pt-1">
                    <button
                      onClick={() => updateLink(idx, { active: !l.active })}
                      title={l.active ? "Sichtbar — klicken zum Ausblenden" : "Ausgeblendet — klicken zum Einblenden"}
                      className={l.active ? "text-green-600" : "text-stone-300"}
                    >
                      {l.active ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                    <button
                      onClick={() => removeLink(idx)}
                      className="text-stone-400 hover:text-red-600"
                      aria-label="Link entfernen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addLink}
              disabled={form.links.length >= 30}
              className="mt-3 flex items-center gap-2 text-sm font-medium text-fox-fire hover:text-orange-600 disabled:opacity-40"
            >
              <Plus size={16} /> Link hinzufügen
            </button>
          </section>

          {/* Veröffentlichen + Speichern */}
          <section className="bg-white rounded-xl border border-stone-200 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setField("published", e.target.checked)}
                className="w-4 h-4 accent-fox-fire"
              />
              <span className="text-sm text-stone-700">
                Öffentlich sichtbar
                <span className="block text-xs text-stone-400">
                  Aus = die Seite ist offline (zeigt „nicht gefunden").
                </span>
              </span>
            </label>
            <button
              onClick={save}
              disabled={!canSave}
              data-testid="lib-save"
              className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-fox-fire text-white font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              {saving ? "Speichern …" : "Speichern"}
            </button>
          </section>
        </div>

        {/* ── Live-Vorschau ── */}
        <div className="lg:sticky lg:top-[132px] self-start">
          <div className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-2 text-center">
            Vorschau
          </div>
          <div className="mx-auto w-full max-w-[320px] rounded-[2rem] border-8 border-stone-800 bg-stone-50 overflow-hidden shadow-xl">
            <div
              className="px-5 pt-8 pb-8 min-h-[480px]"
              style={{ background: `linear-gradient(180deg, ${theme}14 0%, #fafaf9 200px)` }}
            >
              <div className="flex flex-col items-center text-center">
                {isImageSrc(form.avatar_url) ? (
                  <img src={imageSrc(form.avatar_url)} alt="" className="w-20 h-20 rounded-full object-cover ring-4 ring-white shadow" />
                ) : (
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold ring-4 ring-white shadow"
                    style={{ backgroundColor: theme }}
                  >
                    {(form.title || "?").trim().slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="mt-3 text-lg font-bold text-stone-900 break-words">
                  {form.title || "Dein Titel"}
                </div>
                {form.description && (
                  <div className="mt-1 text-sm text-stone-600 whitespace-pre-line">{form.description}</div>
                )}
              </div>
              <div className="mt-6 space-y-2.5">
                {previewLinks.map((l) => (
                  <div
                    key={l.id}
                    className="px-4 py-3 rounded-xl bg-white border shadow-sm text-sm font-medium text-stone-800 truncate text-center"
                    style={{ borderColor: `${theme}33` }}
                  >
                    {l.label}
                  </div>
                ))}
                {previewLinks.length === 0 && (
                  <div className="text-center text-xs text-stone-400 py-4">Deine Links erscheinen hier.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
